import React, { useState, useEffect, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, CircularProgress, Paper, TextField, MenuItem,
} from "@material-ui/core";
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
  Search as SearchIcon, SchoolOutlined as SchoolIcon,
  Visibility as ViewIcon,
} from "@material-ui/icons";
import { Modal, Button as BsButton } from "react-bootstrap";
import SchoolService from "../../../_services/school.service";
import SchoolFormDialog, { SchoolLookups } from "./SchoolFormDialog";
import { useAccessControl } from "../../../access-control/AccessControlContext";

const PAGE_KEY = "school-management";

interface SchoolRow {
  id: number;
  name: string;
  schoolCode: string | null;
  emailId: string | null;
  schoolAddress: string | null;
  location: string | null;
  statusId: number | null;
  statusName: string | null;
  city: string | null;
  schoolTags: string | null;
}

interface Props {
  onAlert: (message: string, severity: "success" | "error" | "info" | "warning") => void;
}

const SchoolTable: React.FC<Props> = ({ onAlert }) => {
  const { canSeeElement } = useAccessControl();
  const canCreate = canSeeElement(PAGE_KEY, "action.create");
  const canEdit = canSeeElement(PAGE_KEY, "action.edit");
  const canDelete = canSeeElement(PAGE_KEY, "action.delete");
  // View is always available; edit/delete are permission-gated.
  const colCount = 9;

  const [rows, setRows] = useState<SchoolRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Applied filters (what the last fetch used) vs. the draft in the inputs.
  const [idInput, setIdInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [franchiseFilter, setFranchiseFilter] = useState<string>("-1");
  const [statusFilter, setStatusFilter] = useState<string>("-1");

  const [lookups, setLookups] = useState<SchoolLookups | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<SchoolRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSchools = useCallback(
    async (pageToLoad: number) => {
      setLoading(true);
      try {
        const res = await SchoolService.search({
          id: idInput.trim() || undefined,
          name: nameInput.trim() || undefined,
          schoolCode: codeInput.trim() || undefined,
          franchise: franchiseFilter !== "-1" ? franchiseFilter : undefined,
          status: statusFilter !== "-1" ? statusFilter : undefined,
          page: pageToLoad,
        });
        if (res.data?.success) {
          setRows(res.data.data || []);
          setTotal(res.data.total || 0);
          setTotalPages(res.data.totalPages || 0);
          setPage(res.data.page ?? pageToLoad);
        } else {
          onAlert(res.data?.error?.message || "Failed to load schools", "error");
        }
      } catch (err: any) {
        const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message;
        onAlert(serverMsg ? `Failed to load schools: ${serverMsg}` : "Failed to load schools", "error");
      } finally {
        setLoading(false);
      }
    },
    [idInput, nameInput, codeInput, franchiseFilter, statusFilter, onAlert],
  );

  // Initial load + lookups (once).
  useEffect(() => {
    fetchSchools(0);
    SchoolService.lookups()
      .then((res) => { if (res.data?.success) setLookups(res.data.data); })
      .catch(() => {/* filters still usable without lookups */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => fetchSchools(0);
  const goToPage = (p: number) => { if (p >= 0 && p < totalPages) fetchSchools(p); };

  const handleCreate = () => { setEditId(null); setViewMode(false); setFormOpen(true); };
  const handleEdit = (id: number) => { setEditId(id); setViewMode(false); setFormOpen(true); };
  const handleView = (id: number) => { setEditId(id); setViewMode(true); setFormOpen(true); };

  const handleFormClose = (saved?: boolean) => {
    setFormOpen(false);
    const wasEdit = editId != null;
    setEditId(null);
    setViewMode(false);
    if (saved) {
      fetchSchools(page);
      onAlert(wasEdit ? "School updated" : "School created", "success");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await SchoolService.remove(deleteTarget.id);
      if (res.data?.success) {
        onAlert("School deleted", "success");
        setDeleteTarget(null);
        fetchSchools(page);
      } else {
        onAlert(res.data?.error?.message || "Failed to delete", "error");
      }
    } catch (err: any) {
      onAlert(err?.response?.data?.error?.message || "Failed to delete school", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="filters-bar">
        <div className="filters-left">
          <div className="cm-search" style={{ minWidth: 110 }}>
            <input
              className="cm-search-input"
              placeholder="ID…"
              type="number"
              value={idInput}
              onChange={(e) => setIdInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <div className="cm-search">
            <SearchIcon className="cm-search-icon" fontSize="small" />
            <input
              className="cm-search-input"
              placeholder="Name (use % or * to wildcard)…"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <div className="cm-search" style={{ minWidth: 180 }}>
            <input
              className="cm-search-input"
              placeholder="School code…"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          {lookups && lookups.franchises.length > 1 && (
            <TextField
              select size="small" variant="outlined" label="Franchise"
              value={franchiseFilter} onChange={(e) => setFranchiseFilter(e.target.value)}
              className="cm-filter-select"
            >
              <MenuItem value="-1">All</MenuItem>
              {lookups.franchises.map((f) => (
                <MenuItem key={f.id} value={String(f.id)}>{f.name}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select size="small" variant="outlined" label="Status"
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="cm-filter-select"
          >
            <MenuItem value="-1">All</MenuItem>
            {(lookups?.statuses || []).map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" color="primary" className="cm-add-btn" onClick={handleSearch} style={{ height: 42 }}>
            Search
          </Button>
        </div>
        {canCreate && (
          <Button
            variant="contained" color="primary" className="cm-add-btn"
            startIcon={<AddIcon />} onClick={handleCreate}
          >
            Add School
          </Button>
        )}
      </div>

      {!loading && (
        <div className="cm-results-meta">
          <strong>{total}</strong> {total === 1 ? "school" : "schools"} found
        </div>
      )}

      <TableContainer component={Paper} elevation={0} className="cm-table-card">
        <Table className="cm-table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Hierarchy</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colCount} align="center" className="cm-state-cell">
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} align="center" className="cm-state-cell">
                  <div className="cm-empty">
                    <SchoolIcon className="cm-empty-icon" />
                    <span>No schools found</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((s) => (
                <TableRow key={s.id} hover className="cm-row">
                  <TableCell className="cm-id">{s.id}</TableCell>
                  <TableCell className="cm-name">{s.name}</TableCell>
                  <TableCell>
                    {s.schoolCode ? <span className="cm-code-chip">{s.schoolCode}</span> : <span className="cm-muted">—</span>}
                  </TableCell>
                  <TableCell>{s.city || <span className="cm-muted">—</span>}</TableCell>
                  <TableCell>{s.emailId ? <span className="cm-email">{s.emailId}</span> : <span className="cm-muted">—</span>}</TableCell>
                  <TableCell className="cm-address" title={s.schoolAddress || ""}>
                    {s.schoolAddress || <span className="cm-muted">—</span>}
                  </TableCell>
                  <TableCell>
                    {s.schoolTags ? (
                      <div className="cm-tag-chips">
                        {s.schoolTags.split(", ").map((t, i) => <span key={i} className="cm-tag-chip">{t}</span>)}
                      </div>
                    ) : <span className="cm-muted">—</span>}
                  </TableCell>
                  <TableCell align="center">
                    <span className={`cm-status-pill ${s.statusName === "Active" ? "status-active" : ""}`}>
                      {s.statusName || "—"}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <div className="cm-actions">
                      <IconButton size="small" className="cm-act cm-act-edit" onClick={() => handleView(s.id)} title="View">
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      {canEdit && (
                        <IconButton size="small" className="cm-act cm-act-edit" onClick={() => handleEdit(s.id)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton size="small" className="cm-act cm-act-del" onClick={() => setDeleteTarget(s)} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <div className="cm-pagination">
          <span>Page <strong>{page + 1}</strong> of {totalPages}</span>
          <div className="cm-pager-controls">
            <Button className="cm-pager-btn" onClick={() => goToPage(0)} disabled={page === 0}>«</Button>
            <Button className="cm-pager-btn" onClick={() => goToPage(page - 1)} disabled={page === 0}>‹</Button>
            <Button className="cm-pager-btn" onClick={() => goToPage(page + 1)} disabled={page >= totalPages - 1}>›</Button>
            <Button className="cm-pager-btn" onClick={() => goToPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</Button>
          </div>
        </div>
      )}

      <SchoolFormDialog
        open={formOpen}
        schoolId={editId}
        lookups={lookups}
        readOnly={viewMode}
        onClose={handleFormClose}
        onError={(msg) => onAlert(msg, "error")}
      />

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete School</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</p>
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
            This also removes its course, program and tag mappings.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <BsButton variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</BsButton>
          <BsButton variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </BsButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SchoolTable;
